// src/sim/catch/drop-off.ts — P3 plinth-zone drop-off (GAME-04 end-state, D-05, D-12).
//
// Invariants:
//   - Pure: returns { updates, events }; never mutates inputs (Pitfall 1).
//   - Drop-off only fires when the escorting hunter's position is inside
//     zoneAt(p) === 'plinth'.
//   - Hider stays status='caught' after parking; free-all flips that.
//   - WorldState.round.caughtCount is NOT mutated here — that counter is
//     incremented by detectCatches (via tick orchestration in Plan 07) and
//     decremented by applyFreeAll. Drop-off only repositions and unlinks.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-05, D-12, D-13.

import type { Entity, WorldState, Vec2 } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import type { GameEvent } from '../../shared/types/events';
import { zoneCenter } from '../map/zones';
import { getPlinthRing, P1_CHARACTER_RADIUS } from '../movement';

export interface DropOffResult {
  updates: Record<string, Entity>;
  events: GameEvent[];
}

/**
 * Per-hider park position on the plinth ring's outer boundary. Caught
 * hiders form a halo around the ring at distance (ringRadius + charRadius)
 * from the ring center. Slot N goes at angle N × (2π / slotsPerRing); when
 * slotsPerRing is exceeded the radius bumps outward by one character
 * diameter so further captives don't overlap.
 *
 * Source: debug session cascade-prevention (2026-04-28).
 */
function parkedHaloPosition(indexAtBase: number, ringCenter: Vec2, ringRadius: number): Vec2 {
  // Circumference of the ring at touch distance ≈ 2π × (ringRadius + charRadius).
  // At charDiameter spacing, slots per ring = floor(circumference / charDiameter).
  // For ringRadius=48, charRadius=16: 2π × 64 ≈ 402, / 32 = 12.5 → 12 slots.
  const touchRadius = ringRadius + P1_CHARACTER_RADIUS;
  const slotsPerRing = Math.max(1, Math.floor((2 * Math.PI * touchRadius) / (P1_CHARACTER_RADIUS * 2)));
  const ringIndex = Math.floor(indexAtBase / slotsPerRing);
  const slot = indexAtBase % slotsPerRing;
  const angle = (slot * 2 * Math.PI) / slotsPerRing;
  const r = touchRadius + ringIndex * P1_CHARACTER_RADIUS * 2;
  return { x: ringCenter.x + Math.cos(angle) * r, y: ringCenter.y + Math.sin(angle) * r };
}

export function applyDropOff(
  world: WorldState,
  mapData: MapData | null | undefined,
  currentTick: number,
): DropOffResult {
  const updates: Record<string, Entity> = {};
  const events: GameEvent[] = [];
  if (!mapData) return { updates, events };
  const ring = getPlinthRing(mapData);
  if (ring === null) return { updates, events };

  // Debug session cascade-prevention (2026-04-28): drop-off fires when the
  // hunter's circle is in CONTACT with the ring (not when they're inside
  // the polygon). Touch threshold = ringRadius + charRadius. The ring
  // collision in clampMotionAgainstWalls already holds the hunter at this
  // distance; we just need to detect the touch.
  const touchThreshold = ring.radius + P1_CHARACTER_RADIUS;
  // Slack ≈ ½ tile (16 px) tolerates sub-pixel jitter from the wall clamp.
  const touchThresholdSq = (touchThreshold + 16) * (touchThreshold + 16);
  const inRingContact = (pos: { x: number; y: number }): boolean => {
    const dx = pos.x - ring.center.x;
    const dy = pos.y - ring.center.y;
    return dx * dx + dy * dy <= touchThresholdSq;
  };

  // Count already-parked hiders to stabilise halo slot indices.
  const alreadyParked = Object.values(world.entities)
    .filter((e) => e.role === 'hider' && e.status === 'caught' && e.escortedBy === null)
    .sort((a, b) => a.id.localeCompare(b.id));
  let parkedIndex = alreadyParked.length;

  // Iterate escorting hunters in deterministic order so stacking-offset
  // assignment is replay-stable across runs.
  const escortingHunters = Object.values(world.entities)
    .filter(
      (h) => h.role === 'hunter' && h.status === 'escorting' && h.escortingEntityId !== null,
    )
    .sort((a, b) => a.id.localeCompare(b.id));

  for (const hunter of escortingHunters) {
    if (!inRingContact(hunter.position)) continue;
    const hiderId = hunter.escortingEntityId!;
    const hider = world.entities[hiderId];
    if (!hider) continue;
    updates[hunter.id] = {
      ...hunter,
      status: 'free',
      escortingEntityId: null,
      recentDropOffTick: currentTick,
    };
    updates[hider.id] = {
      ...hider,
      position: parkedHaloPosition(parkedIndex++, ring.center, ring.radius),
      escortedBy: null,
      // status stays 'caught' (D-05)
      // Debug free-all-misfire-frozen-hider MECHANIC 2 (2026-06-12): stamp the
      // deposit tick so applyFreeAll can enforce the catch->free cooldown and a
      // freshly-deposited captive cannot be instantly re-freed (anti-thrash).
      caughtAtTick: currentTick,
    };
    events.push({
      type: 'hider-parked',
      hunterId: hunter.id,
      hiderId: hider.id,
      tick: currentTick,
    });
  }
  return { updates, events };
}

// Keep zoneCenter import used for future zone-related logic; silence unused.
void zoneCenter;
